import {GengineRoom, GengineClient, IRemoteGame, GenginePlayer} from '../verliba-shared';
import {Board} from './board/board';
import {Snake} from './snake/snake';
import {Serializer} from './serializer';
import {Subscription} from 'rxjs';

export interface IWorldData {
  running?: boolean;
  players: {[name: string]: any};
}

export interface IRoomData {
  snake: any;
  food: any;
  players: {[name: string]: any};
}

export class MultiSnake implements IRemoteGame {

  room?: GengineRoom<IRoomData>;
  player: GenginePlayer<IRoomData, IWorldData>;
  name = 'snake';
  snakeMap: {[player: string]: Snake} = {};
  started = false;

  // noinspection JSUnusedGlobalSymbols
  roomSelect = false;
  private subscription: Subscription;
  myColorScheme = 0;

  constructor(
    private client: GengineClient<IWorldData, IRoomData>,
    private board: Board) {
  }

  // noinspection JSUnusedGlobalSymbols
  setPlayer(player: GenginePlayer<IRoomData, IWorldData>): void {
    this.player = player;
    this.player.events.subscribe(this.onPlayerEvent.bind(this));
  }

  engage(): void {
    this.board.waiting = true;
    const orig = this.board.putFood.bind(this.board);
    this.board.putFood = () => {
      if (this.room.master) {
        const res = orig();
        this.room.data.food = Serializer.foods2json(this.board.food);
        this.room.sendUpdates().catch();
        return res;
      }
    };
  }

  async joinRoom(): Promise<void> {
    await this.client.player.enterRoom(this.board.level.level);
  }

  onRoomUpdate(data: {path: string, value: any}): void {
    if (data.path === 'food') {
      this.board.food = data.value.map(Serializer.json2food);
    }
    if (data.path === '') {
      this.startSlave();
    }
  }

  // noinspection JSUnusedGlobalSymbols
  leaveRoom(): void {
    this.player.leaveRoom();
    delete this.room;
  }

  // noinspection JSUnusedGlobalSymbols
  async leaveGame(): Promise<void> {
    this.board.pause();
    this.player.leaveRoom();
  }

  // noinspection JSUnusedGlobalSymbols
  async startGame(): Promise<void> {
    if (this.room && this.room.master) {
      this.startMaster().catch();
    }
  }

  async onRoomEvent(event: string): Promise<void> {
    try {
      switch (event) {
        case 'enough_users':
          console.log(`got all users`);
          break;
        case 'game_start':
          this.startSlave();
          break;
        case 'master':
          this.room.data = Serializer.board2json(this.board, this.snakeMap);
          this.room.sendUpdates();
          break;
      }
    } catch (e) {
      console.error(e);
    }
  }
  async onBoardEvent(event: string): Promise<void> {
    try {
      switch (event) {
        case 'died':
          this.board.pause();
          const dead = this.board.snakes.splice(0, 1)[0];
          await this.room.command('die', { player: this.player.name});
          if (!dead.lives) {
            this.board.over = true;
            this.leaveRoom();
          } else if (this.room.master) {
            const born = this.board.addSnake(null, true);
            born.tooltip = this.player.name;
            this.snakeMap[this.player.name] = born;
            born.lives = dead.lives;
            born.colorScheme = dead.colorScheme;
            this.room.command('add_snake', {snake: Serializer.snake2json(born), player: this.player.name});
            this.board.countDown(3);
          }
          break;
        case 'move':
          if (this.room.players.length > 1) {
            await this.room.command('move', { player: this.player.name});
          }
          break;
        case 'grow':
          await this.room.command('grow', {
            player: this.player.name,
            grow: this.board.snakes[0].grow
          });
          break;
        case 'level':
          this.board.snakes[0].colorScheme = this.myColorScheme;
          await this.joinRoom();
          break;
      }
    } catch (e) {
      console.error(e);
    }
  }

  private async startMaster(): Promise<void> {
    this.snakeMap = {[this.player.name]: this.board.snakes[0]};
    let schema = 1;
    for (const user of this.room.players) {
      if (user.name === this.player.name) {
        continue;
      }
      const snake = this.board.addSnake();
      this.snakeMap[user.name] = snake;
      snake.tooltip = user.name;
      snake.colorScheme = schema ++;
    }
    this.room.data = Serializer.board2json(this.board, this.snakeMap);
    this.room.sendUpdates();
    this.openBoard();
  }

  private openBoard(): void {
    this.board.waiting = false;
    this.board.multi = true;
    this.board.countDown(3);
    this.board.events.next('restart');
    if (!this.subscription) {
      this.subscription = this.board.events.subscribe(this.onBoardEvent.bind(this));
      this.board.turns.subscribe(async (dir) => {
          await this.room.command('turn', { player: this.player.name, dir });
      });
    }
    this.started = true;
  }

  private startSlave(): void {
    this.snakeMap = Serializer.json2board(this.board, this.room.data, this.player.name);
    this.myColorScheme = this.board.snakes[0].colorScheme;
    this.openBoard();
  }

  private async onCommand(data: {command: string, value: any}): Promise<void> {
    // console.log(data);
    switch (data.command) {
      case 'die':
        const dead = this.snakeMap[data.value.player];
        this.board.gameOver(dead);
        if (!dead.lives) {
          delete this.snakeMap[data.value.player];
        }
        if (this.room.master) {
          if (dead.lives) {
            const born = this.board.addSnake();
            born.tooltip = data.value.player;
            born.lives = dead.lives;
            this.snakeMap[data.value.player] = born;
            born.colorScheme = dead.colorScheme;
            await this.room.command('add_snake', {
              snake: Serializer.snake2json(born),
              player: data.value.player
            });
          }
        }
        break;
      case 'move':
        {
          const snake = this.snakeMap[data.value.player];
          if (snake) {
            this.board.moveSnake(snake);
          }
        }
        break;
      case 'turn':
        this.board.turnSnake(this.snakeMap[data.value.player], data.value.dir);
        break;
      case 'grow':
        this.snakeMap[data.value.player].grow = data.value.grow;
        break;
      case 'add_snake':
        if (!this.player.room.master) {
          const snake = Serializer.json2snake(data.value.snake, data.value.player);
          this.myColorScheme = snake.colorScheme;
          this.snakeMap[data.value.player] = snake;
          const isMe = data.value.player === this.player.name;
          snake.tooltip = data.value.player;
          this.board.addSnake(snake, isMe);
          this.board.pause();
          this.board.countDown(3);
        }
        break;
    }
  }
  private async onPlayerEvent(event: string): Promise<void> {
    switch (event)
    {
      case 'joining room':
        this.room = this.player.room;
        this.room.setUserLimits(1, 20);
        this.room.events.subscribe(this.onRoomEvent.bind(this));
        this.room.playerEvents.subscribe(this.onPlayersEvent.bind(this));
        this.room.updates.subscribe(this.onRoomUpdate.bind(this));
        this.room.commands.subscribe(this.onCommand.bind(this));
        if (this.room.master) {
          this.room.data = Serializer.board2json(this.board, this.snakeMap);
          this.snakeMap[this.player.name] = this.board.snakes[0];
          this.room.sendUpdates();
        }
        break;
    }
  }
  private async onPlayersEvent(data: {event: string, player: string}): Promise<void> {
    switch (data.event) {
      case 'add':
        if (this.player.room.master && this.started) {
          const newer = this.board.addSnake();
          newer.tooltip = data.player;
          for (let i = 0; i < this.board.snakes.length; i++) {
            if (this.board.snakes.every(s => s.colorScheme !== i)) {
              newer.colorScheme = i;
              break;
            }
          }
          this.snakeMap[data.player] = newer;
          this.room.data = Serializer.board2json(this.board, this.snakeMap);
          await this.room.command('add_snake',
            {player: data.player, snake: this.room.data.snake},
            'all_but', data.player);
          await this.room.command('game_start', this.room.data, 'player', data.player);
        }
        break;
      case 'del':
        const snake = this.snakeMap[data.player];
        delete this.snakeMap[data.player];
        this.board.deleteSnake(snake);
        break;
    }
  }

}
